/**
 * Wire schemas for Phase 3 cost control: bills of quantities (synced
 * read-only as `boq`, `boq_version`, `boq_section`, `boq_item`), variations
 * (online only), expenses (synced as `expense`, recorded offline) and the
 * cost summary (online; the last answer is kept for offline viewing).
 *
 * Visibility is decided by the server and mirrored in the shapes: an item
 * carries `rate`/`amount` only for `cost.view`, and `buildup`/`waste_percent`
 * only for `cost.view_internal`; a cost summary carries `actual` and the
 * forecast only when actuals are visible to the caller. The API renders
 * decimals (quantities, percentages) as strings; they are coerced to numbers
 * for display. Money stays `{amount_minor, currency}`.
 */
import { z } from 'zod';

import { moneySchema } from './workforce-types';

const id = z.string();
const nullableId = z.string().nullable().catch(null).default(null);
const date = z.string().nullable().catch(null).default(null);
const text = z.string().catch('').default('');
const decimal = z.coerce.number().catch(0).default(0);
const version = z.number().int().nullable().catch(null).default(null);

// ---------------------------------------------------------------------------
// BOQ
// ---------------------------------------------------------------------------

export const BOQ_STATUSES = ['draft', 'submitted', 'approved', 'superseded'] as const;
export type BoqStatus = (typeof BOQ_STATUSES)[number];
/** Approved and superseded versions are frozen (the server answers 409). */
export const FROZEN_BOQ_STATUSES: readonly string[] = ['approved', 'superseded'];

export const boqSchema = z.object({
  id,
  project_id: id,
  name: text,
  currency: z.string().default('KES'),
  version,
});
export type Boq = z.infer<typeof boqSchema>;

export const boqVersionSchema = z.object({
  id,
  project_id: id,
  boq_id: id,
  number: z.number().int().default(1),
  status: z.string().default('draft'),
  is_baseline: z.boolean().default(false),
  based_on_id: nullableId,
  contingency_percent: decimal,
  notes: text,
  prepared_by_id: nullableId,
  submitted_at: date,
  approved_by_id: nullableId,
  approved_at: date,
  /** Only with cost.view. */
  total: moneySchema.optional(),
  version,
});
export type BoqVersion = z.infer<typeof boqVersionSchema>;

export const boqSectionSchema = z.object({
  id,
  project_id: id,
  version_id: id,
  code: text,
  title: text,
  sequence: z.number().int().catch(0).default(0),
  version,
});
export type BoqSection = z.infer<typeof boqSectionSchema>;

export const BUILDUP_COMPONENTS = ['material', 'labour', 'equipment', 'other', 'profit'] as const;
export type BuildupComponent = (typeof BUILDUP_COMPONENTS)[number];
export const TAX_TREATMENTS = ['exclusive', 'inclusive', 'exempt'] as const;
/** What a bill line is in the contract (module 7): sums are spent on instruction, not measured. */
export const BOQ_ITEM_KINDS = [
  'measured',
  'provisional_sum',
  'prime_cost',
  'daywork',
  'contingency',
] as const;

export const boqItemSchema = z.object({
  id,
  project_id: id,
  version_id: id,
  section_id: id,
  lineage: text,
  item_code: text,
  description: text,
  specification: text,
  unit: text,
  quantity: decimal,
  tax_treatment: z.string().default('exclusive'),
  /** Module 7: measured | provisional_sum | prime_cost | daywork | contingency. */
  kind: z.string().default('measured'),
  stage_id: nullableId,
  product_category: text,
  notes: text,
  sequence: z.number().int().catch(0).default(0),
  /** Only with cost.view. */
  rate: moneySchema.optional(),
  amount: moneySchema.optional(),
  /** Only with cost.view_internal: minor units per unit of measure. */
  buildup: z
    .object({
      material: z.number().int().default(0),
      labour: z.number().int().default(0),
      equipment: z.number().int().default(0),
      other: z.number().int().default(0),
      profit: z.number().int().default(0),
    })
    .optional(),
  waste_percent: z.coerce.number().optional(),
  version,
});
export type BoqItem = z.infer<typeof boqItemSchema>;

/** `GET /projects/{id}/boqs`: each BOQ with its versions, newest first. */
export const boqListEntrySchema = boqSchema.extend({
  versions: z.array(boqVersionSchema).catch([]).default([]),
  current_version_id: nullableId,
  baseline_version_id: nullableId,
  open_version_id: nullableId,
});
export type BoqListEntry = z.infer<typeof boqListEntrySchema>;

/** One version with its BOQ, sections and items (`GET …/boq-versions/{id}`). */
export const boqVersionFullSchema = boqVersionSchema.extend({
  boq: boqSchema,
  sections: z.array(boqSectionSchema).catch([]).default([]),
  items: z.array(boqItemSchema).catch([]).default([]),
});
export type BoqVersionFull = z.infer<typeof boqVersionFullSchema>;

/** Columns an import can fill (spreadsheets.TARGETS); `REQUIRED` must be mapped. */
export const IMPORT_TARGETS = [
  'section',
  'item_code',
  'description',
  'unit',
  'quantity',
  'rate',
  'specification',
  'notes',
] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];
export const IMPORT_REQUIRED: readonly ImportTarget[] = [
  'item_code',
  'description',
  'unit',
  'quantity',
  'rate',
];

export const importPreviewSchema = z.object({
  headers: z.array(z.coerce.string()).default([]),
  sample: z.array(z.array(z.coerce.string())).catch([]).default([]),
  row_count: z.number().int().default(0),
  suggested_mapping: z.record(z.string(), z.number().int()).catch({}).default({}),
});
export type ImportPreview = z.infer<typeof importPreviewSchema>;

export const importResultSchema = z.object({
  imported: z.number().int().default(0),
  errors: z
    .array(z.object({ row: z.number().int(), message: z.string() }))
    .catch([])
    .default([]),
});
export type ImportResult = z.infer<typeof importResultSchema>;

const fromTo = <T extends z.ZodType>(value: T) =>
  z.object({ from: value.nullable().default(null), to: value.nullable().default(null) });

export const compareSchema = z.object({
  changes: z
    .array(
      z.object({
        lineage: text,
        item_code: text,
        description: text,
        change: z.string(),
        quantity: fromTo(z.coerce.number()),
        rate_minor: fromTo(z.number().int()),
        amount_minor_delta: z.number().int().default(0),
      }),
    )
    .catch([])
    .default([]),
  total_delta_minor: z.number().int().default(0),
  currency: z.string().default('KES'),
});
export type BoqComparison = z.infer<typeof compareSchema>;

// ---------------------------------------------------------------------------
// Variations (online only)
// ---------------------------------------------------------------------------

export const VARIATION_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'withdrawn',
] as const;
export const VARIATION_CATEGORIES = [
  'client_instruction',
  'design_change',
  'site_condition',
  'error_omission',
  'regulatory',
  'other',
] as const;
/** Fields the server lets a writer change on a draft (Variation.EDITABLE). */
export const VARIATION_EDITABLE = [
  'title',
  'category',
  'reason',
  'description',
  'original_scope',
  'revised_scope',
  'boq_lineages',
  'cost_impact_minor',
  'time_impact_days',
  'stage_id',
] as const;
export type VariationAction = 'submit' | 'withdraw' | 'approve' | 'reject';

export const variationSchema = z.object({
  id,
  project_id: id,
  number: z.number().int().default(0),
  reference: text,
  title: text,
  category: z.string().default('other'),
  reason: text,
  description: text,
  original_scope: text,
  revised_scope: text,
  boq_lineages: z.array(z.string()).catch([]).default([]),
  stage_id: nullableId,
  cost_impact: moneySchema,
  time_impact_days: z.number().int().catch(0).default(0),
  status: z.string().default('draft'),
  requested_by_id: nullableId,
  requested_by_name: z.string().nullable().catch(null).default(null),
  requested_by_party: text,
  submitted_at: date,
  decided_by_name: z.string().nullable().catch(null).default(null),
  decided_at: date,
  decision_note: text,
  version,
});
export type Variation = z.infer<typeof variationSchema>;

// ---------------------------------------------------------------------------
// Expenses (synced as `expense`; create/update/submit queue offline)
// ---------------------------------------------------------------------------

export const EXPENSE_STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'paid'] as const;
export const EXPENSE_CATEGORIES = [
  'material',
  'equipment',
  'subcontract',
  'transport',
  'other',
] as const;
/** Fields the server lets `expenses.write` set or change (Expense.EDITABLE). */
export const EXPENSE_EDITABLE = [
  'spent_on',
  'category',
  'description',
  'amount_minor',
  'supplier_name',
  'boq_lineage',
  'stage_id',
  'reference',
] as const;
export type ExpenseAction = 'approve' | 'reject' | 'mark_paid';

export const expenseSchema = z.object({
  id,
  project_id: id,
  spent_on: date,
  category: z.string().default('material'),
  description: text,
  /**
   * A record created on this device has no currency until the server
   * answers (it is the project's); screens fall back to the project currency.
   */
  amount: z.object({ amount_minor: z.number().int(), currency: z.string().default('') }),
  supplier_name: text,
  reference: text,
  boq_lineage: nullableId,
  stage_id: nullableId,
  status: z.string().default('draft'),
  recorded_by_id: nullableId,
  decided_at: date,
  decision_note: text,
  paid_at: date,
  payment_reference: text,
  version,
});
export type Expense = z.infer<typeof expenseSchema>;

// ---------------------------------------------------------------------------
// Cost summary (online; last answer cached)
// ---------------------------------------------------------------------------

export const costSummarySchema = z.object({
  currency: z.string().default('KES'),
  commercial_model: z.string().default('open_book'),
  budget: z.object({
    original: moneySchema,
    contingency: moneySchema,
    /** boq_baseline | project_planned_budget */
    source: z.string().default('boq_baseline'),
  }),
  variations: z.object({ approved: moneySchema, pending: moneySchema }),
  revised_budget: moneySchema,
  contingency_remaining: moneySchema,
  earned_value: moneySchema,
  verified_progress_percent: decimal,
  committed: z.object({ total: moneySchema, note: text }),
  boq_count: z.number().int().default(0),
  by_stage: z
    .array(
      z.object({
        /** null = BOQ items not linked to a stage. */
        stage_id: z.string().nullable().default(null),
        stage: z.string().nullable().default(null),
        budget: moneySchema,
        earned: moneySchema,
      }),
    )
    .catch([])
    .default([]),
  by_section: z
    .array(z.object({ section: z.string(), budget: moneySchema }))
    .catch([])
    .default([]),
  methods: z.record(z.string(), z.string()).catch({}).default({}),
  /** "boq_items", or "planned_budget" before any BOQ is approved. */
  earned_value_source: z.string().default('boq_items'),
  // Only when actuals are visible (contractor side; owner side on open-book).
  actual: z
    .object({
      total: moneySchema,
      labour: moneySchema,
      by_category: z.record(z.string(), moneySchema).catch({}).default({}),
    })
    .optional(),
  remaining_budget: moneySchema.optional(),
  forecast_at_completion: moneySchema.optional(),
  variance_at_completion: moneySchema.optional(),
});
export type CostSummary = z.infer<typeof costSummarySchema>;
